/**
 * Small "Need urgent care?" pointer block used on /about, /contact and
 * other static pages to drive internal traffic to the urgent care pillar.
 *
 * Drop-in usage:
 *   <UrgentCarePointerBlock />
 *
 * Wording is safety-first:
 *   • Coordinator language, not provider language
 *   • Not an ambulance / hospital / emergency hotline
 *   • Availability depends on city, clinician, consultation type
 *   • 115 for life-threatening emergencies
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle } from 'lucide-react';

const UrgentCarePointerBlock = ({
  className = '',
  testId = 'urgent-pointer-block',
}) => (
  <section
    className={`py-12 lg:py-16 bg-[#F7FAF9] ${className}`}
    data-testid={testId}
  >
    <div className="container mx-auto px-4 max-w-5xl">
      <div
        className="bg-white rounded-3xl border border-slate-100 p-6 lg:p-10"
        style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
      >
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">
          <div className="flex-1">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
              Urgent care
            </p>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight mb-3">
              Need urgent medical care while in Vietnam?
            </h2>
            <p className="text-sm lg:text-base text-slate-600 leading-relaxed mb-4">
              VietClinics coordinates non-emergency urgent care with verified
              local providers across Vietnam — hotel doctor visits, IV therapy,
              wound care, English-speaking online consultations and clinic
              referrals.
            </p>
            <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-3 flex gap-2 text-xs text-amber-900 leading-relaxed">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p>
                VietClinics is not an ambulance service, hospital or emergency
                hotline. Service availability depends on city, clinician and
                consultation type. For life-threatening emergencies, dial{' '}
                <strong>115</strong> in Vietnam.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 lg:w-[420px] w-full">
            <Link
              to="/urgent-medical-care"
              className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-4 py-3 transition-colors"
              data-testid={`${testId}-pillar`}
            >
              <span className="font-extrabold text-slate-900 text-sm leading-tight">
                Urgent Care Hub
              </span>
              <ArrowRight className="h-4 w-4 text-[#1FA7A1] group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/doctor-to-hotel-vietnam"
              className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-4 py-3 transition-colors"
              data-testid={`${testId}-d2h`}
            >
              <span className="font-extrabold text-slate-900 text-sm leading-tight">
                Doctor-to-Hotel
              </span>
              <ArrowRight className="h-4 w-4 text-[#1FA7A1] group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/english-speaking-doctor-vietnam"
              className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-4 py-3 transition-colors"
              data-testid={`${testId}-esd`}
            >
              <span className="font-extrabold text-slate-900 text-sm leading-tight">
                English-Speaking Drs
              </span>
              <ArrowRight className="h-4 w-4 text-[#1FA7A1] group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/urgent-medical-care-hoi-an"
              className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-4 py-3 transition-colors"
              data-testid={`${testId}-hoian`}
            >
              <span className="font-extrabold text-slate-900 text-sm leading-tight">
                Da Nang &amp; Hoi An
              </span>
              <ArrowRight className="h-4 w-4 text-[#1FA7A1] group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default UrgentCarePointerBlock;
